import {
  Alert,
  Box,
  Button,
  TextField,
  CircularProgress,
  InputAdornment,
  Link as MuiLink,
  Typography,
} from "@mui/material";
import { useState, useReducer } from "react";
import { useNavigate } from "react-router-dom";
import { forgetPassword, resetPassword } from "../../api/auth.js";
import { ArrowBack } from "@mui/icons-material";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import AuthLayout, { authButtonClass } from "../../components/AuthLayout.jsx";
import useSnackbar from "../../hooks/useSnackbar.js";
const initialState = {
  suspense: false,
  err: "",
  step: 1,
  msg: "",
  done: false,
};
const localReducer = (state, action) => {
  switch (action.type) {
    case "SUSPENSE":
      return { ...state, suspense: true, err: "" };
    case "ERROR":
      return { ...state, err: action.payload, suspense: false };
    case "DONE":
      return { ...state, msg: action.payload, suspense: false, done: true };
    case "STEP-2":
      return {
        ...state,
        suspense: false,
        err: "",
        step: 2,
        msg: action.payload,
      };
    case "STEP-1":
      return initialState;
    default:
      return initialState;
  }
};

const emptyData = { email: "", newPassword: "", otp: "" };

export default function ForgetPassword() {
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(localReducer, initialState);
  const snackbar = useSnackbar();
  const [data, setData] = useState(emptyData);
  const handleInputChange = (e) => {
    setData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    dispatch({ type: "SUSPENSE" });
    try {
      const res = await forgetPassword(data.email);
      snackbar.showInfo(res.data.msg);
      dispatch({ type: "STEP-2", payload: "" });
      // dispatch({ type: "STEP-2", payload: res.data.msg });
    } catch (error) {
      const res = error.response?.data;
      dispatch({ type: "ERROR", payload: "" });
      // dispatch({ type: "ERROR", payload: res?.error || error.message });
      snackbar.showError(res.error);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    dispatch({ type: "SUSPENSE" });
    try {
      const res = await resetPassword(data.email, data.otp, data.newPassword);
      snackbar.showInfo(res.data.msg);
      dispatch({ type: "DONE", payload: "" });
      navigate("/login");
      // dispatch({ type: "DONE", payload: res.data.msg });
    } catch (error) {
      const res = error.response?.data;
      dispatch({ type: "ERROR", payload: "" });
      // dispatch({ type: "ERROR", payload: res?.error || error.message });
      snackbar.showError(res.error);
    }
  };

  const submitButton = (label) => (
    <Button type="submit" variant="contained" fullWidth size="large" disabled={state.suspense} className={authButtonClass}>
      {state.suspense ? <CircularProgress size={24} className="!text-white" /> : label}
    </Button>
  );

  return (
    <AuthLayout
      title={state.step === 1 ? "Forgot password?" : "Set a new password"}
      subtitle={
        state.step === 1
          ? "Enter your email and we'll send you a one-time code."
          : `Enter the code sent to ${data.email} and choose a new password.`
      }
      icon={<KeyOutlinedIcon />}
    >
      <Box display="flex" gap={1} mb={3}>
        {[1, 2].map((step) => (
          <Box
            key={step}
            className={`flex-1 h-[5px] rounded-[12px] transition-colors duration-300 ease-[ease] ${state.step >= step ? "bg-[#2f6fd6]" : "bg-[#e3e9f2]"}`}
          />
        ))}
      </Box>

      {Boolean(state.err) ? <Alert severity="error" className="!mb-4">{state.err}</Alert> : null}
      {Boolean(state.msg) ? <Alert severity="success" className="!mb-4">{state.msg}</Alert> : null}

      {state.step === 1 ? (
        <Box component="form" onSubmit={handleEmailSubmit} display="flex" flexDirection="column" gap={2.2}>
          <TextField
            value={data.email}
            type="email"
            label="Email"
            onChange={handleInputChange}
            name="email"
            required
            fullWidth
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MailOutlineIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          {submitButton("Send code")}
        </Box>
      ) : (
        <Box component="form" onSubmit={handleResetPassword} display="flex" flexDirection="column" gap={2.2}>
          <TextField
            value={data.otp}
            label="One-time code"
            onChange={handleInputChange}
            name="otp"
            required
            fullWidth
            autoFocus
            inputProps={{ style: { letterSpacing: 6, fontWeight: 600 } }}
          />
          <TextField
            value={data.newPassword}
            type="password"
            label="New password"
            onChange={handleInputChange}
            name="newPassword"
            required
            fullWidth
            autoComplete="new-password"
          />
          {submitButton("Reset password")}
          {!state.done ? (
            <Typography fontSize={13} color="text.secondary" textAlign="center">
              Wrong email?{" "}
              <MuiLink
                component="button"
                type="button"
                underline="hover"
                fontWeight={600}
                fontSize={13}
                onClick={() => {
                  setData(emptyData);
                  dispatch({ type: "STEP-1" });
                }}
              >
                Change email
              </MuiLink>
            </Typography>
          ) : null}
        </Box>
      )}

      <Box textAlign="center" mt={3}>
        <MuiLink
          component="button"
          type="button"
          underline="hover"
          fontWeight={600}
          fontSize={14}
          onClick={() => navigate("/login", { replace: true })}
          className="!inline-flex !items-center !gap-1"
        >
          <ArrowBack className="!text-[16px]" /> Back to sign in
        </MuiLink>
      </Box>
    </AuthLayout>
  );
}
